// Migration from localStorage to IndexedDB
// Automatically migrates existing trip data without data loss

import storageManager from './storageManager.js';
import Storage from './storageES.js';
import PhotoCompression from './photoCompression.js';

const MigrationToIndexedDB = {

  // ========================================
  // MAIN MIGRATION FUNCTION
  // ========================================

  async migrate() {
    console.log('🔄 Starting migration to IndexedDB...');

    try {
      // 1. Initialize IndexedDB
      await storageManager.initialize();

      // 2. Check if already migrated
      const migrationFlag = localStorage.getItem('indexeddb_migrated');
      if (migrationFlag === 'true') {
        console.log('✅ Already migrated to IndexedDB');
        return { success: true, alreadyMigrated: true };
      }

      // 3. Get old data from localStorage
      const oldSettings = Storage.getSettings();
      const oldTripData = Storage.getTripData();

      console.log('📦 Found data:', {
        settings: oldSettings ? 'Yes' : 'No',
        tripData: oldTripData ? 'Yes' : 'No',
        photos: oldTripData?.photos?.length || 0,
        notes: oldTripData?.notes?.length || 0
      });

      // Check if there's data to migrate
      const hasData = oldSettings && (
        oldSettings.destination ||
        oldSettings.startDate ||
        (oldTripData && (
          oldTripData.photos?.length > 0 ||
          oldTripData.notes?.length > 0 ||
          oldTripData.reflections?.length > 0
        ))
      );

      if (!hasData) {
        console.log('⚠️ No data to migrate - creating fresh start');
        localStorage.setItem('indexeddb_migrated', 'true');

        // Initialize empty trips index
        storageManager.saveTripsIndex({
          activeTrip: null,
          tripIds: [],
          lastModified: new Date().toISOString()
        });

        return { success: true, noData: true };
      }

      // 4. Migrate photos from base64 to blobs
      console.log('📸 Migrating photos...');
      const migratedPhotos = await this._migratePhotos(oldTripData.photos || []);
      console.log(`  ✓ Migrated ${migratedPhotos.length} photos`);

      // 5. Create trip object for IndexedDB
      const tripId = storageManager.generateTripId();

      // Calculate trip duration
      const calculateDuration = (startDate, endDate) => {
        if (!startDate || !endDate) return 1;
        const start = new Date(startDate);
        const end = new Date(endDate);
        const diffTime = Math.abs(end - start);
        return Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
      };

      const duration = calculateDuration(oldSettings.startDate, oldSettings.endDate);

      const tripData = {
        tripId: tripId,
        status: 'active',

        destination: {
          city: oldSettings.destination || oldSettings.location || 'Unknown',
          country: oldSettings.country || 'Unknown',
          coordinates: null
        },

        dates: {
          startDate: oldSettings.startDate || new Date().toISOString().split('T')[0],
          endDate: oldSettings.endDate || new Date().toISOString().split('T')[0],
          totalDays: duration,
          currentDay: this._getCurrentDay(oldSettings.startDate)
        },

        settings: {
          userName: oldSettings.userName || 'TRAVELER',
          theme: oldSettings.theme || 'auto',
          language: 'en'
        },

        timeline: this._buildTimeline(oldTripData, oldSettings),

        content: {
          notes: (oldTripData.notes || []).map(note => ({
            ...note,
            tripId: tripId,
            id: note.id || storageManager.generateNoteId()
          })),
          reflections: (oldTripData.reflections || []).map(refl => ({
            ...refl,
            tripId: tripId,
            id: refl.id || storageManager.generateNoteId()
          })),
          photoIds: migratedPhotos.map(p => p.id),
          challenges: oldTripData.challenges || [],
          luggage: oldTripData.luggage || [],
          groceries: oldTripData.groceries || [],
          gifts: oldTripData.gifts || [],
          lodgings: oldTripData.lodgings || [],
          privateDocuments: oldTripData.privateDocuments || [],
          travelInfo: oldTripData.travelInfo || {
            flights: [],
            cars: [],
            trains: [],
            buses: []
          }
        },

        budget: this._migrateBudget(duration),

        stats: {
          totalMemories: (oldTripData.notes || []).length,
          totalReflections: (oldTripData.reflections || []).length,
          totalPhotos: migratedPhotos.length,
          completedChallenges: (oldTripData.challenges || []).length,
          activitiesPlanned: 0,
          activitiesCompleted: 0,
          daysRecorded: this._countDaysWithNotes(oldTripData.notes || [])
        },

        coverImage: migratedPhotos.length > 0 ? migratedPhotos[0].id : null,

        createdAt: oldSettings.startDate || new Date().toISOString(),
        lastModified: new Date().toISOString(),
        completedAt: null
      };

      // 6. Save to IndexedDB
      console.log('💾 Saving trip to IndexedDB...');
      await storageManager.saveTrip(tripData);
      console.log('  ✓ Trip saved');

      // 7. Update photos with correct tripId
      console.log('🔗 Linking photos to trip...');
      for (const photo of migratedPhotos) {
        photo.tripId = tripId;
        await storageManager.savePhoto(photo);
      }
      console.log('  ✓ Photos linked');

      // 8. Update trips index in localStorage
      const index = {
        activeTrip: tripId,
        tripIds: [tripId],
        lastModified: new Date().toISOString()
      };
      storageManager.saveTripsIndex(index);
      console.log('  ✓ Index updated');

      // 9. Mark as migrated
      localStorage.setItem('indexeddb_migrated', 'true');

      // 10. Create backup of old data (just in case)
      this._createBackup(oldSettings, oldTripData);

      console.log('✅ Migration completed successfully!');
      console.log('📦 Migrated:', {
        tripId: tripId,
        photos: migratedPhotos.length,
        notes: tripData.content.notes.length,
        reflections: tripData.content.reflections.length
      });

      return {
        success: true,
        tripId: tripId,
        stats: {
          photos: migratedPhotos.length,
          notes: tripData.content.notes.length,
          reflections: tripData.content.reflections.length
        }
      };

    } catch (error) {
      console.error('❌ Migration failed:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  // ========================================
  // PHOTO MIGRATION
  // ========================================

  async _migratePhotos(oldPhotos) {
    const migratedPhotos = [];

    if (!oldPhotos || oldPhotos.length === 0) {
      return migratedPhotos;
    }

    console.log(`  📸 Processing ${oldPhotos.length} photos...`);

    for (let i = 0; i < oldPhotos.length; i++) {
      const oldPhoto = oldPhotos[i];

      try {
        // Convert base64 to blob
        const base64Data = oldPhoto.data;
        if (!base64Data) {
          console.warn(`  ⚠️ Photo ${i + 1} has no data, skipping`);
          continue;
        }

        const originalBlob = await PhotoCompression.dataURLToBlob(base64Data);

        // Compress
        const compressedBlob = await PhotoCompression.createCompressed(originalBlob);
        const thumbnailBlob = await PhotoCompression.createThumbnail(originalBlob);

        // Calculate stats
        const stats = PhotoCompression.getCompressionStats(originalBlob.size, compressedBlob.size);
        console.log(`  ✓ Photo ${i + 1}: ${stats.originalMB} MB → ${stats.compressedMB} MB (saved ${stats.savedMB} MB)`);

        // Generate photo ID
        const photoId = oldPhoto.id || storageManager.generatePhotoId();

        // Create photo data
        const photoData = {
          id: photoId,
          tripId: null, // Will be set later
          blob: compressedBlob,
          timestamp: oldPhoto.timestamp || new Date().toISOString(),
          tripDay: oldPhoto.tripDay || 1,
          linkedTo: oldPhoto.linkedTo || null
        };

        // Save to IndexedDB
        await storageManager.savePhoto(photoData);
        await storageManager.saveThumbnail(photoId, thumbnailBlob);

        migratedPhotos.push({ id: photoId });

      } catch (error) {
        console.error(`  ❌ Failed to migrate photo ${i + 1}:`, error.message);
      }
    }

    return migratedPhotos;
  },

  // ========================================
  // BUDGET MIGRATION
  // ========================================

  _migrateBudget(totalDays) {
    const oldBudget = Storage.get('budgetData', {});

    return {
      dailyBudget: oldBudget.dailyBudget || 0,
      totalBudget: (oldBudget.dailyBudget || 0) * totalDays,
      totalSpent: oldBudget.totalSpent || 0,
      spentToday: oldBudget.spentToday || 0,
      spentByDay: [],
      categories: oldBudget.categories || {
        food: 0,
        transport: 0,
        activities: 0,
        shopping: 0,
        accommodation: 0,
        other: 0
      },
      lastResetDate: oldBudget.lastResetDate || new Date().toISOString().split('T')[0]
    };
  },

  // ========================================
  // TIMELINE BUILDER
  // ========================================

  _buildTimeline(tripData, settings) {
    const notes = tripData.notes || [];
    const reflections = tripData.reflections || [];

    // Group by day
    const notesByDay = {};
    notes.forEach(note => {
      const day = note.tripDay || 1;
      if (!notesByDay[day]) notesByDay[day] = [];
      notesByDay[day].push(note.id || storageManager.generateNoteId());
    });

    const reflectionsByDay = {};
    reflections.forEach(refl => {
      const day = refl.tripDay || 1;
      if (!reflectionsByDay[day]) reflectionsByDay[day] = [];
      reflectionsByDay[day].push(refl.id || storageManager.generateNoteId());
    });

    // Calculate total days
    const calculateDuration = (startDate, endDate) => {
      if (!startDate || !endDate) return 7;
      const start = new Date(startDate);
      const end = new Date(endDate);
      const diffTime = Math.abs(end - start);
      return Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
    };

    const totalDays = calculateDuration(settings.startDate, settings.endDate);

    // Build timeline
    const timeline = [];
    for (let day = 1; day <= totalDays; day++) {
      const dayDate = this._addDays(settings.startDate, day - 1);

      timeline.push({
        day: day,
        date: dayDate,
        activities: [],
        memories: notesByDay[day] || [],
        reflections: reflectionsByDay[day] || [],
        budget: { planned: 0, spent: 0 }
      });
    }

    return timeline;
  },

  // ========================================
  // HELPERS
  // ========================================

  _getCurrentDay(startDate) {
    if (!startDate) return 1;
    const start = new Date(startDate);
    const today = new Date();
    const diffTime = today - start;
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24)) + 1;
    return Math.max(1, diffDays);
  },

  _addDays(dateStr, days) {
    if (!dateStr) return new Date().toISOString().split('T')[0];
    const date = new Date(dateStr);
    date.setDate(date.getDate() + days);
    return date.toISOString().split('T')[0];
  },

  _countDaysWithNotes(notes) {
    const days = new Set(notes.map(note => note.tripDay || 1));
    return days.size;
  },

  _createBackup(settings, tripData) {
    try {
      localStorage.setItem('_backup_userSettings', JSON.stringify(settings));
      localStorage.setItem('_backup_tripData', JSON.stringify({
        ...tripData,
        photos: [] // Don't backup photos (too large)
      }));
      console.log('📦 Backup created in localStorage');
    } catch (error) {
      console.warn('⚠️ Could not create backup:', error.message);
    }
  }
};

export default MigrationToIndexedDB;
